import { Component, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/operators';
import { IChartTileDefinition } from '../../../dashboards/interfaces/tiles/definitions/chart-tile-definition.interface';
import { CHART_TYPES } from '../../constants/chart-types.constant';
import { HARDCODED_METRICS } from '../../constants/hardcoded-metrics.constant';
import { SORT_ORDERS } from '../../constants/sort-orders.constant';
import { TileCreatorService } from '../../services/tile-creator.service';
import { TILE_REQUEST } from '../../tokens/tile-request.token';
import { TileSetupWizardComponent } from '../tile-setup-wizard.component';

@Component({
  standalone: false,
  selector: 'app-chart-setup-wizard',
  templateUrl: './chart-setup-wizard.component.html',
  styleUrls: ['./chart-setup-wizard.component.scss'],
})
export class ChartSetupWizardComponent extends TileSetupWizardComponent<IChartTileDefinition> {
  private readonly destroy$ = new Subject<void>();

  readonly setupForm = new FormGroup({
    title: new FormControl(),
    dataSource: new FormControl(),
    chartType: new FormControl(),
    groupBy: new FormControl(),
    metric: new FormControl(),
    sortOrder: new FormControl(),
  });

  dataSourceObject;

  chartTypes = CHART_TYPES;
  sortOrders = SORT_ORDERS;
  availableDataSourceGroups = [];
  availableDataSourceMetrics = [];

  get titleControl(): AbstractControl {
    return this.setupForm.get('title') as AbstractControl;
  }

  get dataSourceControl(): AbstractControl {
    return this.setupForm.get('dataSource') as AbstractControl;
  }

  get chartTypeControl(): AbstractControl {
    return this.setupForm.get('chartType') as AbstractControl;
  }

  get groupByControl(): AbstractControl {
    return this.setupForm.get('groupBy') as AbstractControl;
  }

  get metricControl(): AbstractControl {
    return this.setupForm.get('metric') as AbstractControl;
  }

  get sortOrderControl(): AbstractControl {
    return this.setupForm.get('sortOrder') as AbstractControl;
  }

  constructor(private readonly tileCreatorService: TileCreatorService) {
    super();

    this.tileInstance = inject(TILE_REQUEST).tileInstance;

    if (this.tileInstance) {
      this.setupForm.patchValue({
        title: this.tileInstance.title,
        dataSource: this.tileInstance.recipe?.dataSource,
        chartType: this.tileInstance.chartType?.value,
        groupBy: this.tileInstance.recipe?.groupBy,
        metric: this.tileInstance.recipe?.metric,
        sortOrder: this.tileInstance.recipe?.sortOrder,
      });
    }

    this.dataSourceControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.setupForm.patchValue({
        groupBy: '',
        metric: '',
      });
    });
  }

  ngOnInit() {
    this.hookForm();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initDataSource(dataSource: any) {
    this.dataSourceObject = dataSource;
    this.availableDataSourceGroups = dataSource.properties;
    this.availableDataSourceMetrics = dataSource.properties.filter((property) => {
      return property.type === 'INT';
    });
    this.availableDataSourceMetrics.unshift(...HARDCODED_METRICS);
  }

  onCreate() {
    Object.assign(this.tileInstance, this.getChanges());
    this.tileCreatorService.onChange.next();
  }

  getChanges() {
    return {
      tileType: 'chart',
      title: this.titleControl.value,
      subTitle: '',
      recipe: {
        dataSource: this.dataSourceControl.value,
        metric: this.metricControl.value,
        groupBy: this.groupByControl.value,
        sortOrder: this.sortOrderControl.value,
        maxGroups: 16,
        includeOtherGroup: true,
      },
      chartType: {
        name: 'Line Chart',
        icon: 'show_chart',
        value: this.chartTypeControl.value,
        labels: true,
        trimLabels: false,
        showLegend: false,
        formatLabels: true,
      },
      canView: 'true',
      backgroundColor: 'white',
    };
  }
}
